import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Send } from "lucide-react";
import merchantApi from "../../../lib/merchantApi";
import { CheckboxRow } from "./fields";

/**
 * Step 7 — Review & submit.
 *
 * Submission rules (Brief 2 §5B + §6):
 * - Step 7 is locked unless steps 1–5 are all "complete".
 * - Documents have their own gate: every required + active conditional doc
 *   must be uploaded OR marked N/A. Submit button shows a tooltip when
 *   only the document gate is missing.
 * - Submitting flips status: draft/awaiting_documents → submitted.
 */
export default function Step7Review({
  app, schemas, submitGated, onSubmitted, gotoStep,
}) {
  const stepStatus = app?.step_status || {};
  const incompleteSteps = [1, 2, 3, 4, 5].filter(
    (n) => (stepStatus?.[String(n)] || "incomplete") !== "complete"
  );
  const [attestation, setAttestation] = useState(
    !!app?.step_data?.step7?.accuracy_attestation
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [docsReady, setDocsReady] = useState(null); // null = loading

  // Fetch document readiness on mount + after any submit attempt error.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await merchantApi.get("/merchant/documents");
        if (!cancelled) setDocsReady(!!data.submit_ready);
      } catch {
        if (!cancelled) setDocsReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await merchantApi.post("/merchant/application/submit", {
        accuracy_attestation: attestation,
      });
      onSubmitted?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not submit your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitGated) {
    return (
      <div data-testid="onboarding-step7-locked" className="rounded-3xl border border-amber-400/20 bg-amber-400/[0.03] p-8">
        <AlertTriangle className="h-5 w-5 text-amber-300" />
        <h3 className="font-display mt-3 text-xl text-white tracking-tight">A few things first</h3>
        <p className="mt-3 text-[13.5px] text-zinc-400 leading-relaxed">
          Steps 1 to 5 must be marked <span className="text-white">complete</span> before you can submit.
          Pick up where you left off:
        </p>
        <ul className="mt-5 space-y-2">
          {incompleteSteps.map((n) => {
            const status = stepStatus?.[String(n)] || "incomplete";
            return (
              <li key={n}>
                <button
                  type="button"
                  onClick={() => gotoStep(n)}
                  data-testid={`step7-locked-jump-${n}`}
                  className="group w-full flex items-center justify-between gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.14] transition-colors px-4 py-2.5 text-left"
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                      status === "partial" ? "bg-amber-300" : "bg-zinc-500"
                    }`} />
                    <span className="text-[13px] text-white truncate">
                      Step {n} · {schemas?.[n]?.title || `Step ${n}`}
                    </span>
                  </span>
                  <span className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-400 shrink-0">
                    {status === "partial" ? "in progress" : "not started"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const submitted = ["submitted", "under_review", "approved", "live"].includes(app?.status);
  const docsBlock = !submitted && docsReady === false;
  const submitDisabled = !attestation || submitting || docsBlock;
  const submitTooltip = docsBlock
    ? "Upload or mark N/A on all required documents to submit."
    : !attestation
      ? "Tick the attestation to submit."
      : "";

  return (
    <div data-testid="onboarding-step7" className="space-y-6">
      <div className="rounded-3xl border border-white/[0.06] bg-white/[0.015] p-7">
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Recap</div>
        <h3 className="font-display mt-2 text-2xl text-white tracking-tight">Everything in order?</h3>
        <p className="mt-3 text-[13.5px] text-zinc-400 leading-relaxed">
          Take one last look at the information you've shared. You can still come back to any step
          to refine — but once you submit, it locks for our review.
        </p>

        <ul className="mt-6 space-y-2.5">
          {[1, 2, 3, 4, 5].map((n) => {
            const status = stepStatus?.[String(n)] || "incomplete";
            const Icon = status === "complete" ? CheckCircle2 : AlertTriangle;
            const tone = status === "complete"
              ? "text-emerald-300"
              : status === "partial"
                ? "text-amber-300"
                : "text-zinc-500";
            return (
              <li
                key={n}
                data-testid={`step7-recap-${n}`}
                data-status={status}
                className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] bg-white/[0.015] px-4 py-3"
              >
                <div className="flex items-center gap-2.5">
                  <Icon className={`h-3.5 w-3.5 ${tone}`} />
                  <span className="text-[13px] text-white">{schemas?.[n]?.title || `Step ${n}`}</span>
                  {status !== "complete" && (
                    <span className={`text-[10.5px] uppercase tracking-[0.18em] ${tone}`}>
                      · {status}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => gotoStep(n)}
                  className="text-[12px] text-zinc-400 hover:text-white transition-colors"
                >
                  Edit
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="rounded-3xl border border-white/[0.06] bg-white/[0.015] p-7">
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Attestation</div>
        <div className="mt-4">
          <CheckboxRow
            checked={attestation || submitted}
            onChange={(v) => !submitted && setAttestation(v)}
            label="I confirm the information provided is accurate and complete to the best of my knowledge."
            testId="step7-attestation"
          />
        </div>

        {error && (
          <div data-testid="step7-error" className="mt-5 rounded-xl border border-rose-400/20 bg-rose-400/[0.05] text-rose-200 text-[12.5px] px-4 py-3">
            {error}
          </div>
        )}

        <div className="mt-7 flex items-center gap-3">
          {submitted ? (
            <div data-testid="step7-already-submitted" className="inline-flex items-center gap-2 rounded-full bg-emerald-400/[0.08] border border-emerald-400/30 text-emerald-200 px-4 py-2 text-[13px]">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Submitted — our team will be in touch shortly.
            </div>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitDisabled}
              data-testid="step7-submit-btn"
              title={submitTooltip}
              className="group inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all px-6 py-3 text-[14px] font-medium"
            >
              {submitting ? "Submitting…" : "Submit application"}
              {!submitting && <Send className="h-3.5 w-3.5" />}
            </button>
          )}
          {docsBlock && !submitted && (
            <div data-testid="step7-docs-blocker" className="text-[12px] text-amber-300 inline-flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3" />
              Upload or mark N/A on all required documents to submit.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
